import * as moment from 'moment';

import {
	ContractRates,
	Job,
	JobCurrency,
	QuoteLog,
	QuoteLogAction,
} from 'src/shared/api';
import { DEFAULT_COUNTRY_CODE } from 'src/shared/constants';
import {
	PrevailingWageInformation,
	PrevailingWageRates,
} from 'src/admin-app/shared/prevailing-wage/prevailing-wage.models';
import {
	QuoteDetailGeneralInformation,
	QuoteDetails,
} from './quote-detail.models';

import { BillingDetails } from 'src/shared/billing-details/billing-details.models';
import { LocationDetails } from 'src/shared/job-location/job-location.models';
import { PurchaseOrderDetails } from 'src/admin-app/shared/purchase-order-details/purchase-order-details.models';
import { isEmpty } from 'lodash';
import { jobToOptionalJobFields } from 'src/admin-app/shared/optional-fields/optional-fields.utility';

export function quoteFormToQuote(formValue: QuoteDetails): Required<Job> {
	let quote: Required<Job>;
	if (formValue) {
		const {
			contractedRates,
			fieldContacts,
			documents,
			prevailingWageRates,
			prevailingWageInfo,
			...details
		} = formValue;

		quote = {
			contractedRates,
			fieldContacts,
			documents,
			prevailingWageInfo: {
				...prevailingWageInfo,
				...prevailingWageRates,
			},
			...details.generalInfo,
			...details.billingDetails,
			...details.purchaseOrderDetails,
			...details.optionalJobFields,
			...details.jobLocation,
		};

		if (isEmpty(quote.prevailingWageInfo)) {
			quote.prevailingWageInfo = null;
		}
	}
	return quote;
}

export function quoteToQuoteForm(quote: Job): QuoteDetails {
	if (quote) {
		return {
			generalInfo: quoteToGeneralInfo(quote),
			jobLocation: quoteToJobLocation(quote),
			fieldContacts: quote.fieldContacts,
			documents: quote.documents,
			billingDetails: quoteToBillingDetails(quote),
			purchaseOrderDetails: quoteToPurchaseOrderDetails(quote),
			optionalJobFields: jobToOptionalJobFields(quote),
			contractedRates: quote.contractedRates,
			prevailingWageInfo: quoteToPrevailingWageInfo(quote),
			prevailingWageRates: quoteToPrevailingWageRates(quote),
		};
	} else {
		return {
			generalInfo: null,
			jobLocation: null,
			fieldContacts: [],
			billingDetails: null,
			documents: [],
			purchaseOrderDetails: null,
			optionalJobFields: null,
			contractedRates: null,
			prevailingWageRates: null,
			prevailingWageInfo: null,
		};
	}
}

function quoteToGeneralInfo(quote: Job): QuoteDetailGeneralInformation {
	return {
		id: quote.id,
		flairDocsJobId: quote.flairDocsJobId,
		number: quote.number,
		customer: quote.customer,
		railroad: quote.railroad,
		division: quote.division,
		subdivision: quote.subdivision,
		regularTimeBasis: quote.regularTimeBasis,
		overTimeBasis: quote.overTimeBasis,
		team: quote.team,
		start: quote.start,
		end: quote.end,
		approvalDate: null,
		permitNumber: quote.permitNumber,
		quoteStatus: quote.quoteStatus,
		isSafetyTrainingComplete: quote.isSafetyTrainingComplete,
		descriptionOfSchedule: quote.descriptionOfSchedule,
		equipmentDescription: quote.equipmentDescription,
		description: quote.description,
		notes: quote.notes,
		originatingPortalUserEmail: quote.originatingPortalUserEmail,
		quoteNotificationRecipients: quote.quoteNotificationRecipients,
		dwrNotificationRecipients: quote.dwrNotificationRecipients,
		jobSetupFormRefreshDate: quote.jobSetupFormRefreshDate,
		status: quote.status,
		trackSupervisor: quote.trackSupervisor,
		dateCompleted: quote.dateCompleted,
		lastInvoiceCutoffDate: quote.lastInvoiceCutoffDate,
		assignments: quote.assignments,
		unassignedShifts: quote.unassignedShifts,
		railroadSubCode: quote.railroadSubCode,
		offeredServices: quote.offeredServices || [],
		dateRange: quote.dateRange,
		isNonBillable: quote.isNonBillable,
		orderNumber: quote.orderNumber,
		trackID: quote.trackID,
		party: quote.party,
		jobType: quote.jobType,
		endMarket: quote.endMarket,
		daysRequested: quote.daysRequested,
		billingAddress: quote.billingAddress
	};
}

function quoteToJobLocation(quote: Job): LocationDetails {
	return {
		latitude: quote.latitude,
		longitude: quote.longitude,
		startingMilePost: quote.startingMilePost,
		endingMilePost: quote.endingMilePost,
		distanceFromTracks: quote.distanceFromTracks,
		subdivision: quote.subdivision,
		location: quote.location,
		countryCode: quote.countryCode || DEFAULT_COUNTRY_CODE,
		currency: quote.currency ?? JobCurrency.Usd,
		city: quote.city,
		state: quote.state,
	};
}

function quoteToBillingDetails(quote: Job): BillingDetails {
	return {
		preferredBillingChannel: quote.preferredBillingChannel,
		billTo: quote.billTo,
		otherBillingCompany: quote.otherBillingCompany,
		billingEmails: quote.billingEmails,
		billingNotes: quote.billingNotes,
		billingAddress: quote.billingAddress
	};
}

function quoteToPurchaseOrderDetails(quote: Job): PurchaseOrderDetails {
	return {
		poNumber: quote.poNumber,
		poAmount: quote.poAmount,
		alertAmount: quote.alertAmount,
		estimatedSpend: quote.estimatedSpend,
		alertEmailAddresses: quote.alertEmailAddresses,
		alertThresholdPercentage: quote.alertThresholdPercentage,
	};
}

function quoteToPrevailingWageInfo(quote: Job): PrevailingWageInformation {
	const pw = quote.prevailingWageInfo;
	if (!pw) {
		return null;
	}
	return {
		id: pw.id,
		projectName: pw.projectName,
		projectNumber: pw.projectNumber,
		city: pw.city,
		county: pw.county,
		state: pw.state,
		certifiedPayrollReportsRequired: pw.certifiedPayrollReportsRequired,
		payrollReportsName: pw.payrollReportsName,
		payrollReportsPhone: pw.payrollReportsPhone,
		payrollReportsEmail: pw.payrollReportsEmail,
		confirmedBy: pw.confirmedBy,
		confirmedDate: pw.confirmedDate,
	};
}

function quoteToPrevailingWageRates(quote: Job): PrevailingWageRates {
	const pw = quote.prevailingWageInfo;
	if (!pw) {
		return null;
	}
	return {
		pw1Rate: pw.pw1Rate,
		pw2Rate: pw.pw2Rate,
		pw3Rate: pw.pw3Rate,
		pw1NightShiftRate: pw.pw1NightShiftRate,
		pw2NightShiftRate: pw.pw2NightShiftRate,
		pw3NightShiftRate: pw.pw3NightShiftRate,
	};
}

export function hasRates(entity: {
	contractedRates?: ContractRates[] | ContractRates;
}): boolean {
	if (entity && isArray(entity.contractedRates)) {
		return entity.contractedRates.length > 0;
	} else {
		return (
			entity &&
			entity.contractedRates &&
			Object.values(entity.contractedRates).filter((v) => !!v).length > 0
		);
	}
}

function isArray(obj: any | any[]): obj is [] {
	return obj && obj.length !== undefined;
}

export function buildQuoteLog(
	quoteId: number,
	action: QuoteLogAction,
	message?: string
): QuoteLog {
	return {
		id: 0,
		job: { id: quoteId },
		submissionDate: moment.utc().toJSON(),
		note: message,
		action: action,
	};
}
